import { Link } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { SearchIcon } from "./icons";

/**
 * App shell page header from design system v2.1: title and Space Mono context on the left;
 * search, the Board/Calendar switch and New job on the right. "/" focuses the search.
 */
export function PageHeader({
  title,
  context,
  view,
  search,
  onSearch,
}: {
  title: string;
  context: string;
  view: "board" | "calendar";
  search: string;
  onSearch: (value: string) => void;
}) {
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "/" || e.metaKey || e.ctrlKey || e.altKey) return;
      const target = e.target as HTMLElement;
      if (target.closest("input, textarea, select, [contenteditable=true]")) return;
      e.preventDefault();
      input.current?.focus();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <header className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
      <div className="flex flex-wrap items-baseline gap-x-3.5 gap-y-1">
        <h1 className="shell-title">{title}</h1>
        <span className="font-mono text-[13px] leading-4 text-muted">{context}</span>
      </div>
      <div className="flex flex-wrap items-center gap-2.5">
        <label className="flex h-10 w-full items-center gap-2 rounded-full border border-line bg-bg px-3.5 text-subtle focus-within:border-brand focus-within:ring-4 focus-within:ring-brand-tint sm:w-60">
          <SearchIcon />
          <input
            ref={input}
            type="search"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Escape" && (onSearch(""), e.currentTarget.blur())}
            placeholder="Find a job or customer"
            aria-label="Find a job or customer"
            className="min-w-0 flex-1 bg-transparent text-sm leading-[18px] text-ink outline-none placeholder:text-subtle"
          />
          <kbd className="rounded-[6px] bg-surface px-1.5 py-0.5 font-mono text-[11px] leading-[14px] font-bold text-muted">/</kbd>
        </label>
        <nav className="segmented-canvas" aria-label="View">
          <Link to="/board" className="segment" data-on={view === "board"}>
            Board
          </Link>
          <Link to="/calendar" className="segment" data-on={view === "calendar"}>
            Calendar
          </Link>
        </nav>
        {/* Below lg the shell's top bar already has New job. */}
        <Link to="/jobs/new" className="btn-primary px-[18px] py-[11px] text-sm leading-[18px] max-lg:hidden">
          + New job
        </Link>
      </div>
    </header>
  );
}

/** Lower-cased search terms matched against a job's reference, customer and part. */
export function matchesSearch(search: string, ...fields: Array<string | undefined>): boolean {
  const q = search.trim().toLowerCase();
  if (!q) return true;
  return fields.some((f) => f?.toLowerCase().includes(q));
}
